import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import gql from 'graphql-tag';
import produce from 'immer';
import React, {useMemo} from 'react';

import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getOutputFilesFromStep from 'analysis-tool/data/getOutputFilesFromStep';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DeprecatedDataFrameFragment} from 'data-frames/graphql/GetDataFrame';
import {numericAccessor, stringListAccessor} from 'data-frames/util/AccessorUtils';
import {createAccessor} from 'data-frames/util/createAccessors';
import filterColumns from 'data-frames/util/filterColumns';
import getUserColumns from 'data-frames/util/getUserColumns';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import {isLeafTypeNumerical} from 'data-types/benchling-type-def/BenchlingTypeDef';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import * as MachineLearningActions from 'machine-learning/MachineLearningActions';
import {
  ParameterOptimizationPredictionRequestData,
  ParameterOptimizationPredictionResponse,
} from 'machine-learning/MachineLearningDAO';
import {
  ALL_DATA_DATA_FRAME_NAME,
  FEATURE_CONTOUR_PLOT_FILES_NAME,
  PREDICTION_DEFAULTS_DATA_FRAME_NAME,
  PREDICTIONS_GRID_DATA_FRAME_NAME,
  PREDICTIONS_GRID_PREDICTED_VALUE_COLUMN_SYSTEM_NAME,
} from 'machine-learning/parameter-optimization/legacy-components/ParameterOptimizationConstants';
import FeatureContourPlotsBlock from 'machine-learning/parameter-optimization/prediction/FeatureContourPlotsBlock';
import {FONT_PROXIMA_16, FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  makeAsyncCall,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  GetPredictionStepViewDataFramesQuery,
  GetPredictionStepViewDataFramesQueryVariables,
} from './__generated/GetPredictionStepViewDataFramesQuery';
import PredictionDashboard from './PredictionDashboard';
import PredictionParameters from './PredictionParameters';
import {PredictionParameter, PredictionParametersDict} from './PredictionParametersTypes';

const userInputParametersState = makeUseGlobalClientState<PredictionParametersDict>({});
const previousSubmittedParametersState = makeUseGlobalClientState<Array<PredictionParameter>>([]);
const predictionOutputState =
  makeUseGlobalClientState<
    AsyncState<ParameterOptimizationPredictionResponse, [ParameterOptimizationPredictionRequestData]>
  >(NOT_FETCHED_ASYNC_STATE);

export type ParameterOptimizationPredictionStepViewProps =
  MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION> & {
    allData: DataFrame;
    parameters: Array<PredictionParameter>;
    predictionDefaults: DataFrame;
    predictionsGrid: DataFrame;
  };

export function ParameterOptimizationPredictionStepView(
  props: ParameterOptimizationPredictionStepViewProps
): JSX.Element {
  const [userInputParameters, setUserInputParameters] = userInputParametersState.useGlobalClientState(
    props.clientStateId
  );
  const [previousSubmittedParameters, setPreviousSubmittedParameters] =
    previousSubmittedParametersState.useGlobalClientState(props.clientStateId);
  const [predictionOutput, setPredictionOutput] = predictionOutputState.useGlobalClientState(
    props.clientStateId
  );

  const parametersWithUserInputs = props.parameters.map((parameter) =>
    produce(parameter, (draftParameter) => {
      const userInputValue = userInputParameters[draftParameter.systemName];
      if (userInputValue != null) {
        draftParameter.valueInput.value = userInputValue;
      }
    })
  );

  const memoizedVisualizations = useMemo(() => {
    const columnsToShow = props.predictionDefaults.columns
      .map((column) => column.systemName)
      .concat(PREDICTIONS_GRID_PREDICTED_VALUE_COLUMN_SYSTEM_NAME);

    const featureContourPlotFiles = getOutputFilesFromStep(props.step, FEATURE_CONTOUR_PLOT_FILES_NAME);

    return (
      <>
        {/* Old versions of the modeling step may not have feature contour plot files */}
        {featureContourPlotFiles.length > 0 && (
          <>
            <FeatureContourPlotsBlock contourPlotFiles={featureContourPlotFiles} />
            <div className="u-input-spacing-top" />
          </>
        )}
        <PresentationalUniversalBlockView
          actionOrigin={ActionOrigin.MACHINE_LEARNING}
          inputDataFrame={sortDataFrame(
            filterColumns(props.predictionsGrid, (column) => columnsToShow.includes(column.systemName)),
            {columnKey: PREDICTIONS_GRID_PREDICTED_VALUE_COLUMN_SYSTEM_NAME, sortDirection: 'arrow-up'}
          )}
          paneProps={{
            title: 'Recommendations',
            description: `This table ranks the top parameter values according to predicted output.`,
          }}
          viewStateId={`predictions-grid-${props.clientStateId}`}
          visualizationConfig={makeVisualizationConfig(VisualizationType.TABLE, {displayColumnsJson: null})}
        />
      </>
    );
  }, [props.clientStateId, props.predictionDefaults, props.predictionsGrid, props.step]);

  function handleUpdateParameter(systemName: string, value: string | number): void {
    setUserInputParameters({
      ...userInputParameters,
      [systemName]: value,
    });
  }

  function runPrediction(): void {
    makeAsyncCall({
      makeCall: MachineLearningActions.runParameterOptimizationPrediction,
      args: [
        {
          features: objectFromEntries(
            parametersWithUserInputs.map((parameter) => [parameter.systemName, parameter.valueInput.value])
          ),
          stepId: props.step.id,
        },
      ],
      asyncState: predictionOutput,
      setAsyncState: (nextState) => {
        setPreviousSubmittedParameters(parametersWithUserInputs);
        setPredictionOutput(nextState);
      },
    });
  }

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.columnsContainer)}>
        <div className={css(styles.inputColumn)}>
          <div className={css(styles.title)}>Parameter values</div>
          <div>
            Adjust the parameter values below to see how the predicted output changes. Parameter and output
            values will be rounded to the nearest match in the model's precalculated predictions.
          </div>
          <div className="u-spacing-top">
            <PredictionParameters
              parameters={parametersWithUserInputs}
              onChangeValue={handleUpdateParameter}
            />
          </div>
          <div
            className={classNames(css(styles.resetValues), 'u-spacing-top')}
            onClick={() => {
              setUserInputParameters({});
            }}
          >
            Reset values
          </div>
          <Button
            className="u-spacing-top"
            disabled={predictionOutput.status === AsyncStatus.LOADING}
            size={ButtonSize.MEDIUM}
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={runPrediction}
            text="Run prediction"
          />
        </div>
        <div className={css(styles.outputColumn)}>
          <PredictionDashboard
            allData={props.allData}
            output={predictionOutput}
            parametersForOutput={previousSubmittedParameters}
          />
        </div>
      </div>
      <div className="u-input-spacing-top">{memoizedVisualizations}</div>
    </div>
  );
}

export function getPredictionParametersFromDataFrames(
  predictionDefaultDataFrame: DataFrame,
  predictionsGridDataFrame: DataFrame
): Array<PredictionParameter> {
  return getUserColumns(predictionDefaultDataFrame).map((column) => {
    const {accessor: valueAccessor, validatedLeafType} = createAccessor(
      predictionsGridDataFrame,
      column.systemName
    );

    return {
      displayName: column.displayName,
      systemName: column.systemName,
      valueInput: isLeafTypeNumerical(validatedLeafType)
        ? {
            isDropdown: false,
            value: numericAccessor(valueAccessor, validatedLeafType)(predictionDefaultDataFrame.rows[0]),
            min: Math.min(
              ...predictionsGridDataFrame.rows.map((row) =>
                numericAccessor(valueAccessor, validatedLeafType)(row)
              )
            ),
            max: Math.max(
              ...predictionsGridDataFrame.rows.map((row) =>
                numericAccessor(valueAccessor, validatedLeafType)(row)
              )
            ),
          }
        : {
            isDropdown: true,
            value: stringListAccessor(valueAccessor)(predictionDefaultDataFrame.rows[0])[0],
            options: stringListAccessor(valueAccessor)(predictionDefaultDataFrame.rows[0]),
          },
    };
  });
}

export const GET_PREDICTION_STEP_VIEW_DATA_FRAMES_QUERY = gql`
  query GetPredictionStepViewDataFramesQuery(
    $allDataDataFrameId: StrictString!
    $predictionDefaultsDataFrameId: StrictString!
    $predictionsGridDataFrameId: StrictString!
  ) @skipAudit @engComponent(name: MACHINE_LEARNING) {
    allDataDataFrame: dataFrame(id: $allDataDataFrameId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    predictionDefaultsDataFrame: dataFrame(id: $predictionDefaultsDataFrameId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    predictionsGridDataFrame: dataFrame(id: $predictionsGridDataFrameId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${DeprecatedDataFrameFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingParameterOptimizationPredictionStepView({
  step,
  ...restProps
}: Omit<
  ParameterOptimizationPredictionStepViewProps,
  'allData' | 'parameters' | 'predictionDefaults' | 'predictionsGrid'
>): JSX.Element {
  return (
    <BenchlingQuery<GetPredictionStepViewDataFramesQuery, GetPredictionStepViewDataFramesQueryVariables>
      query={GET_PREDICTION_STEP_VIEW_DATA_FRAMES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        allDataDataFrameId: findAndAssertSingleStepDataFrameId(step, ALL_DATA_DATA_FRAME_NAME, true),
        predictionDefaultsDataFrameId: findAndAssertSingleStepDataFrameId(
          step,
          PREDICTION_DEFAULTS_DATA_FRAME_NAME,
          false
        ),
        predictionsGridDataFrameId: findAndAssertSingleStepDataFrameId(
          step,
          PREDICTIONS_GRID_DATA_FRAME_NAME,
          false
        ),
      }}
    >
      {(data) => {
        if (
          isInaccessible(data.allDataDataFrame) ||
          isInaccessible(data.predictionDefaultsDataFrame) ||
          isInaccessible(data.predictionsGridDataFrame)
        ) {
          assertAndContinue(false, 'Got inaccessible dataframe within an analysis');
          return (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have access to the datasets for this analysis."
            />
          );
        }

        const predictionParameters = getPredictionParametersFromDataFrames(
          data.predictionDefaultsDataFrame.dataFrameJson,
          data.predictionsGridDataFrame.dataFrameJson
        );
        return (
          <ParameterOptimizationPredictionStepView
            {...restProps}
            allData={data.allDataDataFrame.dataFrameJson}
            step={step}
            parameters={predictionParameters}
            predictionDefaults={data.predictionDefaultsDataFrame.dataFrameJson}
            predictionsGrid={data.predictionsGridDataFrame.dataFrameJson}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_TITLE,
    ...FONT_PROXIMA_16,
  },
  columnsContainer: {
    display: 'flex',
  },
  inputColumn: {
    flex: '1 1 0',
  },
  outputColumn: {
    marginLeft: 16,
    flex: '2 1 0',
  },
  resetValues: {
    ...FONT_PROXIMA_TITLE,
    color: BLUE_LIGHT,
    cursor: 'pointer',
  },
  scatterPlot: {
    height: 400,
  },
  wrapper: {
    backgroundColor: GRAY_LIGHTER,
    padding: 16,
  },
});
